var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { paginatedQuery } from 'lib-shared-api-utils';
import { api } from 'utils/apis/api';
import { transformReadConversationMessageDeliveryEvents, transformReadConversationMessagesResponse, transformReadConversationResponse, transformReadConversationsResponse, transformReadScheduledConversationBatchesResponse, } from './utils/transformResponse';
export const conversationsApi = api.injectEndpoints({
    endpoints: (builder) => ({
        readConversations: builder.query({
            query: (arg) => paginatedQuery(arg, (arg) => ({
                version: 3,
                path: 'conversations',
                params: getConversationsParams(arg),
            }), {
                pagination: { paramNaming: 'pageSizePageNumber' },
                sort: { paramName: 'sort_by' },
            }),
            transformResponse: transformReadConversationsResponse,
        }),
        readRequesterConversations: builder.query({
            query: ({ userId }) => ({
                version: 2,
                path: `conversations/requester/${userId}`,
                params: {
                    limit: 500,
                    attributes: [
                        'id',
                        'status',
                        'created_at',
                        'initial_channel',
                        'direction',
                        'attributes',
                    ].join(','),
                },
            }),
        }),
        readConversation: builder.query({
            query: ({ csid }) => ({
                version: 2,
                path: `conversations/${csid}`,
            }),
            transformResponse: transformReadConversationResponse,
        }),
        updateConversationAssignmentTakeover: builder.mutation({
            query: ({ csid }) => ({
                version: 1,
                path: `conversations/${csid}/claim`,
                method: 'PUT',
            }),
        }),
        updateConversationAssignmentAnswer: builder.mutation({
            query: ({ csid, didAccept: did_accept }) => ({
                version: 1,
                path: `conversations/${csid}/respond`,
                method: 'PUT',
                body: { did_accept },
            }),
        }),
        updateConversationClose: builder.mutation({
            query: ({ csid }) => ({
                version: 1,
                path: `conversations/${csid}/close`,
                method: 'PUT',
            }),
        }),
        updateConversationWorkTimeComplete: builder.mutation({
            query: ({ csid }) => ({
                version: 1,
                path: `conversations/${csid}/acw`,
                method: 'PUT',
            }),
        }),
        updateConversationWorkTimeExtend: builder.mutation({
            query: ({ csid, duration: duration_in_seconds }) => ({
                version: 1,
                path: `conversations/${csid}/acw`,
                method: 'DELETE',
                body: { duration_in_seconds },
            }),
        }),
        readConversationActivityLog: builder.query({
            query: ({ csid }) => ({
                version: 1,
                path: `conversations/${csid}/activity_log`,
            }),
        }),
        readConversationFlowActivities: builder.query({
            query: ({ csid }) => ({
                version: 1,
                path: `conversations/${csid}/flow_activities`,
            }),
        }),
        updateConversationLanguage: builder.mutation({
            query: ({ csid, language }) => ({
                version: 1,
                path: `conversations/${csid}/language`,
                method: 'POST',
                body: { language },
            }),
        }),
        updateConversationTag: builder.mutation({
            query: ({ action, csid, tag }) => (Object.assign({ version: 1 }, (action === 'add'
                ? {
                    path: `conversations/${csid}/tags`,
                    method: 'POST',
                    body: { name: tag.name },
                }
                : {
                    path: `conversations/${csid}/tags/${tag.name}`,
                    method: 'DELETE',
                }))),
            onQueryStarted({ action, csid, tag }, { dispatch, queryFulfilled }) {
                return __awaiter(this, void 0, void 0, function* () {
                    const patchResult = dispatch(conversationsApi.util.updateQueryData('readConversation', { csid }, (draft) => {
                        const tags = draft.data.history.main_conversation.tags;
                        if (action === 'add') {
                            tags.unshift(tag);
                        }
                        else {
                            tags.splice(tags.findIndex(({ name }) => name === tag.name), 1);
                        }
                    }));
                    try {
                        yield queryFulfilled;
                    }
                    catch (_a) {
                        patchResult.undo();
                    }
                });
            },
        }),
        createConversationFromEmail: builder.mutation({
            query: ({ from: from_email, to: to_email, cc: cc_emails, bcc: bcc_emails, attachments: attachments_v2, contentType: content_type, wrapup: internal_note, queueId: queue_id, shouldOffer: offer, parentConversationLink, tags, subject, body, language, }) => ({
                version: 1,
                path: 'conversations/email',
                method: 'POST',
                body: Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({ from_email,
                    to_email,
                    subject,
                    body,
                    content_type }, ((cc_emails === null || cc_emails === void 0 ? void 0 : cc_emails.length) && { cc_emails })), ((bcc_emails === null || bcc_emails === void 0 ? void 0 : bcc_emails.length) && { bcc_emails })), ((attachments_v2 === null || attachments_v2 === void 0 ? void 0 : attachments_v2.length) && { attachments_v2 })), ((tags === null || tags === void 0 ? void 0 : tags.length) && { tags: tags.map(({ id }) => id) })), (typeof language !== 'undefined' && { language })), (typeof queue_id !== 'undefined' && { queue_id })), (typeof offer !== 'undefined' && { offer })), (typeof internal_note !== 'undefined' && { internal_note })), (typeof parentConversationLink !== 'undefined' && {
                    link: Object.assign({ parent_csid: parentConversationLink.csid }, (typeof parentConversationLink.type !== 'undefined' && {
                        link_type: parentConversationLink.type,
                    })),
                })),
            }),
        }),
        createConversationFromSms: builder.mutation({
            query: ({ from: from_phone_number, to: to_phone_number, queueId: queue_id, tags, message: body, }) => ({
                version: 1,
                path: 'conversations/sms',
                method: 'POST',
                body: Object.assign(Object.assign({ from_phone_number,
                    to_phone_number,
                    body }, ((tags === null || tags === void 0 ? void 0 : tags.length) && { tags: tags.map(({ id }) => id) })), { queue_id }),
            }),
        }),
        readConversationMessages: builder.query({
            query: ({ csid }) => ({
                version: 1,
                path: `conversations/${csid}/messages`,
            }),
            transformResponse: transformReadConversationMessagesResponse,
        }),
        createConversationMessage: builder.mutation({
            query: ({ csid, text, channel, from, to: to_emails, cc: cc_emails, bcc: bcc_emails, contentType: content_type, attachments: attachedfiles_v2, }) => ({
                version: 1,
                path: `conversations/${csid}/messages`,
                method: 'POST',
                body: Object.assign(Object.assign(Object.assign(Object.assign({ channel }, (typeof to_emails !== 'undefined' && { to_emails })), (typeof cc_emails !== 'undefined' && { cc_emails })), (typeof bcc_emails !== 'undefined' && { bcc_emails })), { attributes: Object.assign(Object.assign(Object.assign(Object.assign({}, (typeof from !== 'undefined' && { from })), (typeof attachedfiles_v2 !== 'undefined' && {
                        attachedfiles_v2,
                    })), (typeof content_type !== 'undefined' &&
                        content_type === 'html' && { content_type })), { text }) }),
            }),
        }),
        createConversationWrapupMessage: builder.mutation({
            query: ({ csid, text: message }) => ({
                version: 1,
                path: `conversations/${csid}/wrapup`,
                method: 'POST',
                body: { message },
            }),
        }),
        updateConversationBlindAssignmentTransfer: builder.mutation({
            query: (arg) => ({
                version: 1,
                path: `conversations/${arg.csid}/transfer/blind`,
                method: 'PUT',
                body: Object.assign(Object.assign({ to: arg.to }, (arg.entity === 'text' && arg.text && { message: arg.text })), (arg.entity === 'number' && { entity: arg.entity })),
            }),
        }),
        updateConversationBlindAssignmentTransferAnswer: builder.mutation({
            query: ({ csid, didAccept: did_accept }) => ({
                version: 1,
                path: `conversations/${csid}/transfer/answer`,
                method: 'PUT',
                body: { did_accept },
            }),
        }),
        updateConversationAssistedAssignmentTransfer: builder.mutation({
            query: ({ csid, userId }) => ({
                version: 1,
                path: `conversations/${csid}/transfer/assisted`,
                method: 'PUT',
                body: { to: userId },
            }),
        }),
        updateConversationAssistedAssignmentTransferAnswer: builder.mutation({
            query: ({ csid, didAccept: accepted }) => ({
                version: 1,
                path: `conversations/${csid}/transfer/assisted/resolve`,
                method: 'PUT',
                body: { accepted },
            }),
        }),
        updateConversationQueueTransfer: builder.mutation({
            query: ({ csid, queueId: queue_id }) => ({
                version: 1,
                path: `conversations/${csid}/transfer/queue`,
                method: 'PUT',
                body: { queue_id },
            }),
        }),
        updateConversationPendingStatus: builder.mutation({
            query: ({ csid, timestamp, queueId: queue_id }) => ({
                version: 1,
                method: 'PUT',
                path: `conversations/${csid}/pending`,
                body: Object.assign({ timestamp }, (queue_id && { queue_id })),
            }),
        }),
        updateConversationCancelPending: builder.mutation({
            query: ({ csid }) => ({
                version: 1,
                method: 'PUT',
                path: `conversations/${csid}/cancel_pending`,
            }),
        }),
        createConversationsCsv: builder.mutation({
            query: ({ email: to_email, dateRange: [from_datetime, to_datetime], }) => ({
                version: 1,
                path: 'conversations/exports',
                method: 'POST',
                body: { to_email, from_datetime, to_datetime },
            }),
        }),
        readScheduledConversationBatches: builder.query({
            query: () => ({
                version: 1,
                path: 'conversations/scheduled/batches',
            }),
            transformResponse: transformReadScheduledConversationBatchesResponse,
        }),
        createScheduledConversationBatch: builder.query({
            query: ({ name, date, outboundNumber: outbound_number, queueId: queue_id, csvPath: csv_url, }) => ({
                version: 1,
                path: 'conversations/scheduled',
                method: 'POST',
                body: {
                    channel: 'callback',
                    outbound_number,
                    name,
                    date,
                    queue_id,
                    csv_url,
                },
            }),
            onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                return __awaiter(this, void 0, void 0, function* () {
                    try {
                        const { data: { data: batch }, } = yield queryFulfilled;
                        dispatch(conversationsApi.util.updateQueryData('readScheduledConversationBatches', undefined, (draft) => {
                            const mutableBatches = draft.data.batches;
                            mutableBatches.unshift(batch);
                        }));
                    }
                    catch (_a) {
                        // do nothing
                    }
                });
            },
        }),
        updateConversationCallRecordingPlayback: builder.mutation({
            query: ({ csid, state }) => ({
                version: 1,
                path: `conversations/${csid}/recordings/${state}`,
            }),
        }),
        updateConversationMuteCall: builder.mutation({
            query: ({ csid, mute }) => ({
                version: 1,
                path: `conversations/${csid}/mute`,
                body: { mute },
            }),
        }),
        updateConversationHoldCall: builder.mutation({
            query: ({ csid, userId, hold }) => ({
                version: 1,
                path: `conversations/${csid}/hold`,
                body: { hold, userId },
            }),
        }),
        updateConversationCaller: builder.mutation({
            query: ({ csid, userId, action }) => ({
                version: 2,
                path: `conversations/${csid}/${action}`,
                method: 'PUT',
                body: { userId },
            }),
        }),
        readConversationsIdsBulk: builder.query({
            query: (arg) => ({
                version: 3,
                path: 'conversations/bulk/ids',
                params: getConversationsParams(arg),
            }),
        }),
        readConversationsValidationBulk: builder.query({
            query: ({ csids }) => ({
                version: 3,
                method: 'POST',
                path: 'conversations/bulk/validation',
                body: { csids },
            }),
        }),
        createConversationMessageBulk: builder.mutation({
            query: ({ csids, plainText: plain_text, htmlText: html_text }) => ({
                version: 1,
                path: 'conversations/bulk/reply',
                method: 'POST',
                body: { csids, plain_text, html_text },
            }),
        }),
        createConversationWrapupMessageBulk: builder.mutation({
            query: ({ csids, text: message }) => ({
                version: 1,
                path: 'conversations/bulk/wrapup',
                method: 'POST',
                body: { csids, message },
            }),
        }),
        updateConversationTagBulk: builder.mutation({
            query: ({ csids, names: tag_names, action }) => ({
                version: 1,
                path: 'conversations/bulk/tag',
                method: action === 'add_tag' ? 'POST' : 'DELETE',
                body: { csids, tag_names },
            }),
        }),
        updateConversationCloseBulk: builder.mutation({
            query: ({ csids }) => ({
                version: 1,
                path: 'conversations/bulk/close',
                method: 'POST',
                body: { csids },
            }),
        }),
        updateConversationCancelCsatBulk: builder.mutation({
            query: ({ csids }) => ({
                version: 1,
                path: 'conversations/bulk/cancel_csat',
                method: 'POST',
                body: { csids },
            }),
        }),
        updateConversationQueueTransferBulk: builder.mutation({
            query: ({ csids, queueId: new_queue_id }) => ({
                version: 1,
                path: 'conversations/bulk/transfer/queue',
                method: 'POST',
                body: { csids, new_queue_id },
            }),
        }),
        readConversationMessageDeliveryEvents: builder.query({
            query: ({ csid, messageId, filter }) => ({
                version: 4,
                path: `conversations/${csid}/messages/${messageId}/delivery-events`,
                params: {
                    filter,
                },
            }),
            transformResponse: transformReadConversationMessageDeliveryEvents,
        }),
        readOrGenerateSummary: builder.query({
            query: ({ csid, regenerate, view }) => ({
                version: 4,
                method: 'POST',
                path: `conversations/${csid}/summary`,
                params: { regenerate },
                body: { view },
            }),
        }),
    }),
});
/**
 * Gets the conversations params. This is moved to a function as it is shared
 * between the endpoints `readConversations` and `readConversationsIdsBulk`.
 *
 * @param arg The conversation api arg.
 */
function getConversationsParams(arg) {
    return Object.assign(Object.assign({}, (typeof arg.agentIds === 'boolean'
        ? { assigned: arg.agentIds }
        : { agent: arg.agentIds })), { channel: arg.channels, direction: arg.directions, queue: arg.queueIds, requester: arg.requesterIds, status: arg.status });
}
